import type { ReactNode } from 'react';
import { Ban, CalendarClock, ClipboardList, IdCard, PencilLine, ShieldCheck, Trash2, Truck } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { ChangeHistory } from '../../../components/ui/ChangeHistory';
import { findEligibleVehicles } from '../../../core/driver-matcher';
import { hasPermission } from '../../../core/permissions';
import { formatPhone } from '../../../core/phone';
import { vehicleCallSign } from '../../../core/military-id';
import { soldierRoleLabel } from '../../../core/soldier-roles';
import {
  describeSoldierStatus,
  isIndefiniteStatus,
  isTimedStatusActive,
} from '../../../core/soldier-status';
import { APP_CONFIG } from '../../../core/config';
import {
  SOLDIER_AVAILABILITY_LABELS,
  VEHICLE_PERMIT_LABELS,
  WEIGHT_CATEGORY_LABELS,
} from '../../../types/fleet';
import type { Soldier, Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { AVAILABILITY_STYLES } from './SoldierCard';

interface SoldierDetailsModalProps {
  soldier: Soldier;
  vehicles: readonly Vehicle[];
  user: User;
  now?: number;
  onClose: () => void;
  onEdit: (soldier: Soldier) => void;
  onChangeStatus: (soldier: Soldier) => void;
  /** Hands the soldier to the roster's delete confirmation. */
  onDelete?: (soldier: Soldier) => void;
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(APP_CONFIG.locale, { timeZone: APP_CONFIG.timeZone }) : '—';

function Field({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-ink/50">{label}</dt>
      <dd className="text-sm" data-dir={ltr ? 'ltr' : undefined}>
        {value}
      </dd>
    </div>
  );
}

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
      {icon}
      {children}
    </h3>
  );
}

/**
 * The full picture for one soldier: who they are, what their licence covers,
 * which vehicles that opens, and what the command blocked. The vehicle list
 * comes from the matching engine, never from a second copy of the rules.
 */
export function SoldierDetailsModal({
  soldier,
  vehicles,
  user,
  now = Date.now(),
  onClose,
  onEdit,
  onChangeStatus,
  onDelete,
}: SoldierDetailsModalProps) {
  const canManage = hasPermission(user, 'MANAGE_SOLDIERS');
  const timed = isTimedStatusActive(soldier.temporaryStatus, now);
  /** A running status with no deadline — nothing will bring the soldier back. */
  const openEnded = timed && isIndefiniteStatus(soldier.temporaryStatus);
  const style = AVAILABILITY_STYLES[soldier.availability];

  const allowed = findEligibleVehicles(soldier, vehicles, { now, ignoreAvailability: true });
  const byId = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]));
  const blocked = soldier.disallowedVehicleIds
    .map((id) => byId.get(id))
    .filter((vehicle): vehicle is Vehicle => Boolean(vehicle));

  return (
    <Modal
      size="lg"
      title={soldier.fullName}
      subtitle={[soldierRoleLabel(soldier.role), soldier.company, soldier.platoon]
        .filter(Boolean)
        .join(' · ')}
      onClose={onClose}
      footer={
        canManage ? (
          <>
            {onDelete ? (
              <button
                type="button"
                onClick={() => onDelete(soldier)}
                className="h-10 px-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-medium hover:bg-rose-100 flex items-center gap-2 me-auto"
              >
                <Trash2 size={16} aria-hidden />
                מחיקת חייל
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onChangeStatus(soldier)}
              className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-2"
            >
              <CalendarClock size={16} aria-hidden />
              שינוי סטטוס
            </button>
            <button
              type="button"
              onClick={() => onEdit(soldier)}
              className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center gap-2"
            >
              <PencilLine size={16} aria-hidden />
              עריכת חייל
            </button>
          </>
        ) : null
      }
    >
      <div className="space-y-5">
        <section
          className={`rounded-2xl border p-4 ${timed ? 'border-brand-100 bg-brand-50' : 'border-line'}`}
        >
          <SectionTitle icon={<CalendarClock size={16} className="text-brand-500" aria-hidden />}>
            סטטוס נוכחי
          </SectionTitle>
          <p className="text-base font-semibold">{describeSoldierStatus(soldier, now)}</p>
          {timed ? (
            <p className="mt-1 text-xs text-ink/60">
              {openEnded
                ? 'סטטוס זמני ללא מועד סיום — יישאר בתוקף עד לשינוי ידני על ידי מפקד'
                : `סטטוס זמני — עם חלוף המועד יחזור החייל ל״${SOLDIER_AVAILABILITY_LABELS[soldier.availability]}״`}
              {soldier.temporaryStatus?.note ? ` · ${soldier.temporaryStatus.note}` : ''}
            </p>
          ) : (
            <p className="mt-1 text-xs text-ink/50 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${style.dot}`} aria-hidden />
              סטטוס קבוע ברשומה
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<IdCard size={16} className="text-ink/50" aria-hidden />}>
            פרטים אישיים
          </SectionTitle>
          <dl className="grid grid-cols-2 gap-3">
            <Field label="מספר אישי" value={soldier.personalNumber} ltr />
            <Field label="טלפון" value={formatPhone(soldier.phone)} ltr />
            <Field label="תפקיד" value={soldierRoleLabel(soldier.role)} />
            <Field label="פלוגה" value={soldier.company} />
            <Field label="מחלקה" value={soldier.platoon || 'לא שויכה'} />
          </dl>
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<ShieldCheck size={16} className="text-emerald-600" aria-hidden />}>
            רישיונות והרשאות
          </SectionTitle>
          <dl className="grid grid-cols-2 gap-3 mb-3">
            <Field
              label="דרגת רישיון"
              value={
                soldier.licenseWeightCategory
                  ? WEIGHT_CATEGORY_LABELS[soldier.licenseWeightCategory]
                  : 'ללא רישיון צבאי'
              }
            />
            <Field label="תוקף הרישיון" value={formatDate(soldier.licenseExpiresAt)} />
          </dl>
          <div className="flex flex-wrap gap-1.5">
            {soldier.licensePermits.map((permit) => (
              <span
                key={permit}
                className="text-xs px-2 py-1 rounded-lg border border-brand-100 bg-brand-50 text-brand-700"
              >
                {VEHICLE_PERMIT_LABELS[permit]}
              </span>
            ))}
            {soldier.licensePermits.length === 0 ? (
              <span className="text-xs text-ink/50">אין הרשאות תפעול מיוחדות.</span>
            ) : null}
          </div>
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<Truck size={16} className="text-brand-500" aria-hidden />}>
            רכבים מורשים ({allowed.length})
          </SectionTitle>
          {allowed.length === 0 ? (
            <p className="text-sm text-ink/50">הרישיון הנוכחי אינו מכסה אף רכב במצבת.</p>
          ) : (
            <ul className="space-y-1.5">
              {allowed.map((match) => {
                const vehicle = byId.get(match.vehicleId);
                if (!vehicle) return null;
                return (
                  <li
                    key={match.vehicleId}
                    className="flex items-center justify-between gap-3 text-sm rounded-xl border border-line px-3 py-2"
                  >
                    <span className="truncate">
                      <span className="font-bold tabular-nums" data-dir="ltr">
                        {vehicleCallSign(vehicle)}
                      </span>{' '}
                      · {vehicle.name || vehicle.model}
                    </span>
                    <span className="shrink-0 text-xs text-ink/50 tabular-nums">{match.score}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {blocked.length > 0 ? (
          <section className="rounded-2xl border border-line p-4">
            <SectionTitle icon={<Ban size={16} className="text-rose-600" aria-hidden />}>
              רכבים מוחרגים ({blocked.length})
            </SectionTitle>
            <ul className="space-y-1.5">
              {blocked.map((vehicle) => (
                <li
                  key={vehicle.id}
                  className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2"
                >
                  <span data-dir="ltr">{vehicleCallSign(vehicle)}</span> ·{' '}
                  {vehicle.name || vehicle.model}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Who moved this soldier's status, and when. Every quick-status tap
            and every form save files a row, so this is the answer to
            "מי ביצע את השינוי" without leaving the card. */}
        <ChangeHistory
          entityType="soldier"
          entityId={soldier.id}
          revision={soldier.updatedAt}
          emptyText="טרם בוצעו שינויים ברשומת החייל."
        />

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<ClipboardList size={16} className="text-ink/50" aria-hidden />}>
            היסטוריית משימות
          </SectionTitle>
          {/* The tasks layer lands in a later task; the section is wired and empty. */}
          <p className="text-sm text-ink/50">טרם שובץ החייל למשימות שינוע.</p>
        </section>

        <section className="rounded-2xl border border-line p-4">
          <SectionTitle icon={<PencilLine size={16} className="text-ink/50" aria-hidden />}>
            הערות מפקד
          </SectionTitle>
          <p className="text-sm text-ink/70 whitespace-pre-line">
            {soldier.notes || 'אין הערות רשומות.'}
          </p>
        </section>
      </div>
    </Modal>
  );
}
